/**
 * SubgroupManagerDialog
 *
 * Dedicated editor for managing subgroups (nested subsections) inside an
 * Organizer. Users can:
 *   - Create a new subgroup (name + optional icon + optional color)
 *   - Rename an existing subgroup
 *   - Delete a subgroup (its achievements return to the organizer's ungrouped bucket)
 *   - Drag-reorder subgroups
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  GripVertical,
  Plus,
  Trash2,
  Save,
  Layers,
  Star,
  Flag,
  Target,
  Sword,
  Sparkles,
  Zap,
  Trophy,
  Heart,
  Compass,
  Book,
  Skull,
  Key,
  X,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const SUBGROUP_ICONS = [
  { key: "Star", Comp: Star },
  { key: "Flag", Comp: Flag },
  { key: "Target", Comp: Target },
  { key: "Sword", Comp: Sword },
  { key: "Sparkles", Comp: Sparkles },
  { key: "Zap", Comp: Zap },
  { key: "Trophy", Comp: Trophy },
  { key: "Heart", Comp: Heart },
  { key: "Compass", Comp: Compass },
  { key: "Book", Comp: Book },
  { key: "Skull", Comp: Skull },
  { key: "Key", Comp: Key },
];

export const SUBGROUP_COLORS = [
  "#F59E0B", // amber
  "#EF4444", // red
  "#10B981", // emerald
  "#06B6D4", // cyan
  "#3B82F6", // blue
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#84CC16", // lime
];

export function getSubgroupIconComp(key) {
  const found = SUBGROUP_ICONS.find(i => i.key === key);
  return found ? found.Comp : Layers;
}

function SortableSubgroupRow({ sub, onChange, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sub.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComp = getSubgroupIconComp(sub.icon);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded-md"
      data-testid={`subgroup-row-${sub.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        style={{ touchAction: "none" }}
        className="p-2 -m-1 text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Icon picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            style={sub.color ? { color: sub.color } : {}}
            title="Change icon"
            data-testid={`subgroup-icon-${sub.id}`}
          >
            <IconComp className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2 bg-[#0A0A0A] border-white/10">
          <div className="grid grid-cols-6 gap-1">
            <button
              onClick={() => onChange({ icon: null })}
              className="p-1.5 rounded hover:bg-white/10 text-white/40"
              title="No icon"
            >
              <X className="w-4 h-4" />
            </button>
            {SUBGROUP_ICONS.map(({ key, Comp }) => (
              <button
                key={key}
                onClick={() => onChange({ icon: key })}
                className={`p-1.5 rounded hover:bg-white/10 ${
                  sub.icon === key ? "bg-white/10 ring-1 ring-cyan-500/50" : ""
                }`}
                title={key}
              >
                <Comp className="w-4 h-4 text-white/80" />
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Color picker */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="h-6 w-6 rounded border border-white/20 shrink-0"
            style={{ backgroundColor: sub.color || "transparent" }}
            title="Change color"
            data-testid={`subgroup-color-${sub.id}`}
          >
            {!sub.color && <span className="text-white/30 text-xs">—</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2 bg-[#0A0A0A] border-white/10">
          <div className="grid grid-cols-4 gap-1.5">
            <button
              onClick={() => onChange({ color: null })}
              className="h-8 rounded border border-white/20 text-white/40 text-xs flex items-center justify-center hover:bg-white/10"
            >
              None
            </button>
            {SUBGROUP_COLORS.map(c => (
              <button
                key={c}
                onClick={() => onChange({ color: c })}
                className={`h-8 rounded border ${
                  sub.color === c ? "ring-2 ring-white/60" : "border-white/10"
                }`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Input
        value={sub.name}
        onChange={e => onChange({ name: e.target.value })}
        placeholder="Subgroup name"
        className="flex-1 h-8 bg-black/50 border-white/10 text-sm"
        data-testid={`subgroup-name-input-${sub.id}`}
      />

      <Button
        variant="ghost"
        size="icon"
        onClick={onDelete}
        className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
        title="Delete subgroup"
        data-testid={`subgroup-delete-${sub.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

export function SubgroupManagerDialog({
  open,
  onOpenChange,
  gameId,
  section,
  api,
  onChanged,
}) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());

  // Reset local state whenever the dialog opens for a new section
  useEffect(() => {
    if (open) {
      setItems((section?.subsections || []).map(s => ({ ...s })));
      setDirtyIds(new Set());
      setNewName("");
    }
  }, [open, section?.id]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Long-press activates drag on touch so page scrolling still works.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const orderChanged = useMemo(() => {
    const original = section?.subsections || [];
    if (items.length !== original.length) return true;
    return items.some((it, idx) => original[idx]?.id !== it.id);
  }, [items, section]);

  const markDirty = (id) => {
    setDirtyIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Enter a name for the subgroup");
      return;
    }
    try {
      setSaving(true);
      const res = await axios.post(
        `${api}/games/${gameId}/sections/${section.id}/subsections`,
        { name }
      );
      setItems(prev => [...prev, res.data]);
      setNewName("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create subgroup");
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (id, patch) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
    markDirty(id);
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(
      `Delete "${sub.name || 'this subgroup'}"? Its achievements will return to the organizer's ungrouped list.`
    )) return;
    try {
      setSaving(true);
      await axios.delete(
        `${api}/games/${gameId}/sections/${section.id}/subsections/${sub.id}`
      );
      setItems(prev => prev.filter(it => it.id !== sub.id));
      setDirtyIds(prev => {
        const next = new Set(prev);
        next.delete(sub.id);
        return next;
      });
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete subgroup");
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const oldIdx = prev.findIndex(it => it.id === active.id);
      const newIdx = prev.findIndex(it => it.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      // Persist any field changes
      for (const id of dirtyIds) {
        const item = items.find(it => it.id === id);
        if (!item) continue;
        await axios.put(
          `${api}/games/${gameId}/sections/${section.id}/subsections/${id}`,
          { name: item.name, icon: item.icon, color: item.color }
        );
      }
      // Persist reorder
      if (orderChanged && items.length > 0) {
        await axios.post(
          `${api}/games/${gameId}/sections/${section.id}/subsections/reorder`,
          { orderedIds: items.map(it => it.id) }
        );
      }
      toast.success("Subgroups saved");
      setDirtyIds(new Set());
      onChanged && onChanged();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = dirtyIds.size > 0 || orderChanged;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 text-white max-w-lg"
        data-testid="subgroup-manager-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-cyan-400" />
            Subgroups · {section?.name || "Organizer"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Nested groups let you sub-sort achievements inside this organizer.
            Drag to reorder. Deleting a subgroup returns its achievements to the
            organizer&apos;s ungrouped list.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          {items.length === 0 && (
            <p className="text-sm text-white/40 text-center py-4">
              No subgroups yet. Add one below.
            </p>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map(it => it.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {items.map(sub => (
                  <SortableSubgroupRow
                    key={sub.id}
                    sub={sub}
                    onChange={(patch) => handleFieldChange(sub.id, patch)}
                    onDelete={() => handleDelete(sub)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className="pt-2 border-t border-white/10">
            <Label className="text-xs text-white/60">Add subgroup</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleAdd();
                }}
                placeholder="e.g., Chapter 1, Multiplayer, Side Missions..."
                className="flex-1 h-9 bg-black/50 border-white/10 text-sm"
                data-testid="subgroup-new-name-input"
              />
              <Button
                onClick={handleAdd}
                disabled={saving || !newName.trim()}
                className="h-9 bg-cyan-600 hover:bg-cyan-700"
                data-testid="subgroup-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/10">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/70 hover:text-white"
            >
              Close
            </Button>
            <Button
              onClick={handleSaveAll}
              disabled={saving || !hasChanges}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="subgroup-save-btn"
            >
              <Save className="w-4 h-4 mr-1" /> Save changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SubgroupManagerDialog;
